import { ScrollArea } from "@mantine/core";
import { useToggle } from "@mantine/hooks";
import { Group } from "../../group";
import { InteractionIcon } from "../../interaction-icon";
import { Modal } from "../../modal";
import { IProps } from "./interaction-modal.types";
import { Title } from "../../title";
import { Icon } from "../../icon";
import { ActionIcon } from "../../action-icon";
import {
    faArrowDownLeftAndArrowUpRightToCenter,
    faArrowUpRightAndArrowDownLeftFromCenter,
    faChevronDown,
    faChevronUp,
    faEllipsis,
    faUserVneck,
} from "@fortawesome/pro-solid-svg-icons";
import { Text } from "../../text";
import { Stack } from "../../stack";
import { Chat } from "./chat";
import { SidebarContent } from "./sidebar-content/sidebar-content";
import { useInteractionModalContext } from "./context/context";
import { DetailsSidebar } from "./details-sidebar";
import { Tooltip } from "../../tooltip";
import { Fragment, useEffect, useMemo, useRef } from "react";
import { Skeleton } from "../../skeleton";
import { AutoMenu } from "../../auto-menu";
import { Menu } from "../../menu";
import { InteractionType } from "@edgetier/watchtower-types";
import { RIGHT_SIDEBAR_WIDTH } from "./interaction-modal.constants";
import "./interaction-modal.scss";
import { TranslateButton } from "./translate-button";

const InteractionModal = ({ onClose, ...otherProps }: IProps) => {
    const { interaction, interactionNavigation, actions, elements } = useInteractionModalContext();
    const [isFullScreen, toggleFullScreen] = useToggle([false, true]);

    const interactionSubject = useMemo(() => {
        if (
            !interaction.data ||
            typeof interaction.data.subject !== "string" ||
            interaction.data.subject.length === 0
        ) {
            return "--";
        }
        const translation = actions?.translation?.activeTranslations[interaction.data.interactionId]?.translatedText;
        if (actions?.translation?.isEnabled && translation) {
            return translation;
        }
        return interaction.data.subject;
    }, [interaction.data, actions?.translation]);

    const lastTrackedInteractionIdRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        const currentInteractionId = interaction.data?.interactionId;
        if (
            currentInteractionId &&
            currentInteractionId !== lastTrackedInteractionIdRef.current &&
            typeof actions?.tracking?.trackUser === "function"
        ) {
            lastTrackedInteractionIdRef.current = currentInteractionId;
            actions.tracking.trackUser(currentInteractionId);
        }
    }, [interaction.data?.interactionId, actions?.tracking]);

    useEffect(() => {
        if (!otherProps.opened) {
            lastTrackedInteractionIdRef.current = undefined;
        }
    }, [otherProps.opened]);

    /**
     * If the AudioProvider is present, we want to wrap the modal body with it so that the audio player and the
     * interaction has access to its context.
     */
    const ModalBodyWrapper = useMemo(() => {
        return elements?.audio?.audioProvider ?? Fragment;
    }, [elements?.audio?.audioProvider]);

    return (
        <Modal.Root
            size="min(90%, 1360px)"
            centered
            onClose={() => {
                onClose?.();
                isFullScreen && toggleFullScreen();
            }}
            {...otherProps}
            fullScreen={isFullScreen}
        >
            <Modal.Overlay />
            <Modal.Content h={"100%"}>
                <Modal.Header>
                    <Group flex={1} justify="space-between" wrap="nowrap" maw="100%">
                        <Modal.Title style={{ overflow: "hidden" }}>
                            <Group flex={1} wrap="nowrap">
                                {interaction.isLoading && <InteractionIcon.Skeleton size="input-sm" />}
                                {!interaction.isLoading && interaction.data && (
                                    <InteractionIcon size="input-sm" type={interaction.data?.interactionType} />
                                )}
                                {interaction.isLoading && <Title.Skeleton order={4} width={200} />}
                                {!interaction.isLoading && (
                                    <Skeleton
                                        miw={0}
                                        visible={
                                            (actions?.translation?.isEnabled &&
                                                actions?.translation?.activeTranslations[
                                                    interaction.data?.interactionId ?? ""
                                                ]?.isLoading) ??
                                            false
                                        }
                                    >
                                        <Title order={4} textWrap="nowrap" lineClamp={1}>
                                            {interactionSubject}
                                        </Title>
                                    </Skeleton>
                                )}
                            </Group>
                        </Modal.Title>
                        <Group wrap="nowrap">
                            <Tooltip.Group openDelay={500} closeDelay={100}>
                                <Group gap="xs" wrap="nowrap">
                                    {actions?.translation && <TranslateButton />}
                                    {actions?.customerOverview && (
                                        <Tooltip label="See Customer Details">
                                            <ActionIcon
                                                variant="default"
                                                size="input-xs"
                                                onClick={actions.customerOverview.onClick}
                                                disabled={actions.customerOverview.isDisabled}
                                                data-testid="customer-details-button"
                                            >
                                                <Icon icon={faUserVneck} />
                                            </ActionIcon>
                                        </Tooltip>
                                    )}
                                    {actions?.moreActions &&
                                        ("items" in actions.moreActions ? (
                                            <AutoMenu position="bottom-end" items={actions.moreActions.items}>
                                                <ActionIcon
                                                    variant="default"
                                                    size="input-xs"
                                                    data-testid="interaction-options-menu"
                                                >
                                                    <Icon icon={faEllipsis} />
                                                </ActionIcon>
                                            </AutoMenu>
                                        ) : (
                                            <Menu>
                                                <Menu.Target>
                                                    <ActionIcon
                                                        variant="default"
                                                        size="input-xs"
                                                        data-testid="interaction-options-menu"
                                                    >
                                                        <Icon icon={faEllipsis} />
                                                    </ActionIcon>
                                                </Menu.Target>
                                                <Menu.Dropdown>{actions.moreActions.menu}</Menu.Dropdown>
                                            </Menu>
                                        ))}
                                </Group>
                                <Group gap={0} wrap="nowrap">
                                    <Tooltip label={isFullScreen ? "Collapse" : "Expand"}>
                                        <ActionIcon
                                            onClick={() => toggleFullScreen()}
                                            variant="subtle"
                                            size="input-xs"
                                            color="gray"
                                            data-testid="toggle-fullscreen-button"
                                        >
                                            <Icon
                                                icon={
                                                    isFullScreen
                                                        ? faArrowDownLeftAndArrowUpRightToCenter
                                                        : faArrowUpRightAndArrowDownLeftFromCenter
                                                }
                                            />
                                        </ActionIcon>
                                    </Tooltip>
                                    <Tooltip label="Close">
                                        <Modal.CloseButton />
                                    </Tooltip>
                                </Group>
                            </Tooltip.Group>
                        </Group>
                    </Group>
                </Modal.Header>
                <Modal.BodyWithSidebar>
                    <Modal.BodyWithSidebar.Sidebar>
                        <SidebarContent />
                    </Modal.BodyWithSidebar.Sidebar>
                    <Modal.BodyWithSidebar.Content>
                        <ModalBodyWrapper key={interaction.data?.interactionId}>
                            <Group h="100%" gap="xs" style={{ overflow: "hidden" }} className="interaction-pdf-content">
                                <ScrollArea flex={1} h="100%" offsetScrollbars="y">
                                    <Chat />
                                </ScrollArea>
                                <ScrollArea w={RIGHT_SIDEBAR_WIDTH} h={"100%"}>
                                    <Stack className="interaction-details">
                                        <DetailsSidebar />
                                    </Stack>
                                </ScrollArea>
                            </Group>
                            <Modal.Footer>
                                <Group justify="space-between">
                                    <Group flex={1} grow>
                                        {typeof elements?.audio?.audioPlayer === "undefined" ||
                                        interaction.data?.interactionType !== InteractionType.Call
                                            ? null
                                            : elements.audio.audioPlayer()}
                                    </Group>
                                    <Group gap="sm" w={RIGHT_SIDEBAR_WIDTH} justify="flex-end">
                                        {interactionNavigation?.showInteractionCount && (
                                            <Text size="sm">
                                                {(interactionNavigation?.currentIndex ?? 0) + 1} of{" "}
                                                {interactionNavigation?.total}
                                            </Text>
                                        )}
                                        <ActionIcon.Group>
                                            <ActionIcon
                                                variant="default"
                                                size="input-xs"
                                                onClick={interactionNavigation?.onPrevious}
                                                data-testid="previous-interaction-button"
                                            >
                                                <Icon icon={faChevronUp} />
                                            </ActionIcon>
                                            <ActionIcon
                                                variant="default"
                                                size="input-xs"
                                                disabled={!interactionNavigation?.hasNext}
                                                onClick={interactionNavigation?.onNext}
                                                data-testid="next-interaction-button"
                                            >
                                                <Icon icon={faChevronDown} />
                                            </ActionIcon>
                                        </ActionIcon.Group>
                                    </Group>
                                </Group>
                            </Modal.Footer>
                        </ModalBodyWrapper>
                    </Modal.BodyWithSidebar.Content>
                </Modal.BodyWithSidebar>
            </Modal.Content>
        </Modal.Root>
    );
};
export { InteractionModal };
